import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Loader2, Package } from 'lucide-react';
import StoreNav from '../components/store/StoreNav';
import PublicSiteFooter from '../components/store/PublicSiteFooter';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { resolveThumbnailUrl } from '../lib/publicApi';

export default function MyLibraryPage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingSlug, setDownloadingSlug] = useState(null);

  useEffect(() => {
    if (searchParams.get('purchased') === '1') {
      toast.success('Purchase successful! Your download is ready below.');
    }
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/my-library');
        setItems(res.data.items || []);
      } catch {
        toast.error('Could not load your library');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const handleDownload = async (slug) => {
    setDownloadingSlug(slug);
    try {
      const res = await api.get(`/my-library/${slug}/download`);
      const url = res.data.download_url;
      if (!url) throw new Error('No download URL returned');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingSlug(null);
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <StoreNav />
      <main className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="mb-8 space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">My Library</h1>
          <p className="text-muted-foreground">Everything you&apos;ve purchased — download anytime.</p>
        </div>

        {loading && (
          <div className="flex min-h-[200px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">You haven&apos;t purchased any templates or toolkits yet.</p>
              <Button asChild><Link to="/templates">Browse Templates &amp; Toolkits</Link></Button>
            </CardContent>
          </Card>
        )}

        {!loading && items.length > 0 && (
          <div className="grid gap-4">
            {items.map((item) => (
              <Card key={item.product_slug} data-testid={`library-item-${item.product_slug}`}>
                <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                    {item.thumbnail_url ? (
                      <img src={resolveThumbnailUrl(item.thumbnail_url)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-muted-foreground" /></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-lg">{item.product_name}</h2>
                      {item.category && <Badge variant="secondary" className="capitalize">{item.category}</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Purchased {item.purchased_at ? new Date(item.purchased_at).toLocaleDateString() : '—'}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDownload(item.product_slug)}
                    disabled={downloadingSlug === item.product_slug}
                    data-testid={`download-${item.product_slug}`}
                  >
                    {downloadingSlug === item.product_slug ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    Download
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      <PublicSiteFooter />
    </div>
  );
}
